/* eslint-disable @typescript-eslint/naming-convention */
import { useContext, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';

import {
  getExpiredDtAPI,
  getMBTIUserByUuidAPI,
  getSurveyAPI,
  postSurveyAPI,
  updateSurveyAPI,
} from '@api/controller';
import {
  GET_EXPIRED_DT_ACTION,
  GET_SURVEY_ACTION,
  POST_SURVEY_ACTION,
  UPDATE_SURVEY_ACTION,
} from '@constants/actions';
import {
  B2C_VERSION,
  exceptYnSurveys,
  Page,
  requiredSurveys,
  surveyObj,
  Gender,
  omitReqSurveyCdByIsrcSurveyCd,
} from '@constants/enum';
import PageIndexContext from '@containers/Context/PageIndexContext';
import { useReduxForm } from '@hooks/useReduxForm';
import { useRequest } from '@hooks/useRequest';
import _ from 'lodash';
import moment from 'moment';
import { uuid as genUuid } from 'uuidv4';

export function useBody() {
  const { pageIndex, setPageIndex } = useContext(PageIndexContext);
  const { params = { id: undefined, uuid: undefined } } = useRouteMatch<{
    id?: string;
    uuid?: string;
  }>();
  const { formData, setFormData } = useReduxForm('survey');
  const { setFormData: setB2cFormData } = useReduxForm('b2cform');
  const { setFormData: setMbtiFlowData } = useReduxForm('mbtiFlow');

  const {
    requestForSingle: expiredDtRequest,
    resData: expiredDtResData = {},
    requestLoading: expiredDtRequestLoading,
    requestSuccess: expiredDtRequestSuccess,
  } = useRequest({
    reduxActionName: GET_EXPIRED_DT_ACTION,
    controllerFunc: getExpiredDtAPI,
  });

  const {
    requestForSingle: surveyRequest,
    resData: { data: { getSurvey: surveyResData } } = { data: { getSurvey: {} } },
    requestLoading: surveyRequestLoading,
    requestSuccess: surveyRequestSuccess,
  } = useRequest({
    reduxActionName: GET_SURVEY_ACTION,
    controllerFunc: getSurveyAPI,
  });

  const {
    requestForSingle: postSurvey,
    resData: { data: { createSurvey: postSurveyData } } = { data: { createSurvey: {} } },
    requestSuccess: postSurveySuccess,
  } = useRequest({
    reduxActionName: POST_SURVEY_ACTION,
    controllerFunc: postSurveyAPI,
  });

  const {
    requestForSingle: surveyUpdate,
    resData: { data: { updateSurvey: updatedData } } = { data: { updateSurvey: {} } },
  } = useRequest({
    reduxActionName: UPDATE_SURVEY_ACTION,
    controllerFunc: updateSurveyAPI,
  });

  useEffect(() => {
    // /mbti/:uuid 라우트 — 알림톡 링크로 진입
    if (params.uuid) {
      document.title = '한의진단 MBTI';
      // 흰 화면 제거: 데이터 fetch 끝나기 전에 즉시 시작 화면 노출 (UUID flow 한정)
      setPageIndex(Page.B2CMain);
      (async () => {
        const user = await getMBTIUserByUuidAPI(params.uuid as string);
        if (!user) {
          setPageIndex(Page.NotFoundError);
          return;
        }
        // KakaoShare 숨김 등 후속 분기에서 사용
        setMbtiFlowData({
          uuid: user.uuid,
          name: user.name,
          gender: user.gender,
          age: user.age,
        });
        // 기존 B2C 분기 호환 위해 b2cform Redux도 채움
        setB2cFormData({ name: user.name, gender: user.gender, age: user.age });

        const lsKey = `mbti-survey-id-${user.uuid}`;
        // 서버에 완료 표시가 있으면 — 본인이든 공유 받은 제3자든 — 그 결과만 표시.
        // 공유로 진입한 제3자가 새 설문을 시작하지 못하도록 localStorage를 덮어쓴다.
        if (user.completedSurveyId) {
          localStorage.setItem(lsKey, user.completedSurveyId);
          localStorage.setItem('survey-id', user.completedSurveyId);
          localStorage.setItem('survey-name', user.name);
          localStorage.setItem('survey-version', B2C_VERSION);
          surveyRequest({ id: user.completedSurveyId });
          return;
        }
        const storedId = localStorage.getItem(lsKey);
        if (storedId) {
          // 이미 시작한 설문이 있으면 이어서
          localStorage.setItem('survey-id', storedId);
          localStorage.setItem('survey-name', user.name);
          localStorage.setItem('survey-version', B2C_VERSION);
          surveyRequest({ id: storedId });
        } else {
          // 새 Survey 생성 후 DetailSurvey로 자동 진행
          const newId = `${moment().format('YYYYMMDD')}${genUuid()}`;
          localStorage.setItem(lsKey, newId);
          localStorage.setItem('survey-id', newId);
          localStorage.setItem('survey-name', user.name);
          localStorage.setItem('survey-version', B2C_VERSION);
          postSurvey({
            id: newId,
            surveyType: 'B2C',
            clinic: 'B2C_Clinic',
            patient: {
              patientName: user.name,
              gender: user.gender,
              birth: moment().add(-user.age, 'years').format('YYYY-MM-DD'),
            },
          });
        }
      })();
      return;
    }
    if (params.id === 'survey-v1') {
      const id = localStorage.getItem('survey-id');
      const name = localStorage.getItem('survey-name');
      const version = localStorage.getItem('survey-version');
      if (id && name && version === B2C_VERSION) {
        surveyRequest({ id });
      } else {
        setPageIndex(Page.B2CMain);
      }
    } else if (params.id) {
      document.title = '예진 증상설문';
      // survey secret key가 주어진경우
      expiredDtRequest({ id: params.id });
    } else {
      document.title = '예진 증상설문';
      // 기본 url만 있는 경우, NotFoundError
      setPageIndex(Page.NotFoundError);
    }
  }, []);

  useEffect(() => {
    if (expiredDtRequestSuccess) {
      if (!expiredDtResData?.expriedStatus && !expiredDtResData?.progressingTreatmentFlag) {
        // 설문기한이 만료되지 않았다면
        if (expiredDtResData?.id) {
          surveyRequest({ id: expiredDtResData.id });
        } else {
          // secret에 해당하는 데이터 없음
          setPageIndex(Page.NotFoundError);
        }
      } else if (expiredDtResData?.progressingTreatmentFlag) {
        // 진료중이라면
        setPageIndex(Page.TreatmentIsProgressing);
      } else {
        // 설문기한이 만료되었다면
        setPageIndex(Page.Expired);
      }
    }
  }, [expiredDtRequestSuccess]);

  useEffect(() => {
    if (surveyRequestSuccess) {
      if (surveyResData) {
        const { id, visitCount, _version, completedDt, surveyType } = surveyResData;
        if (completedDt) {
          // 설문을 완료했었다면
          if (surveyType === 'B2C') {
            setPageIndex(Page.B2CResult);
          } else {
            setPageIndex(Page.Completed);
          }
          // surveyUpdate({ id, visitCount: visitCount + 1, _version });
        } else {
          surveyUpdate({ id, visitCount: visitCount + 1, _version });
        }
      } else {
        postSurvey(expiredDtResData);
      }
    }
  }, [surveyRequestSuccess]);

  useEffect(() => {
    if (postSurveySuccess) {
      const { id, visitCount, _version } = postSurveyData;
      surveyUpdate({ id, visitCount: visitCount + 1, _version });
    }
  }, [postSurveySuccess]);

  useEffect(() => {
    if (updatedData?.id) {
      const { id, _version, mainSymptom, rawData, patient, surveyType } = updatedData;
      // console.log('updatedData', updatedData);
      let parsedData = { visitedSurvey: [], currentSurvey: undefined, surveyList: [] };
      let surveyMainKeyArr = _.keys(surveyObj);
      if (typeof rawData === 'string') {
        parsedData = JSON.parse(rawData);
      }
      const { visitedSurvey, currentSurvey } = parsedData;
      const body: any = {
        ...parsedData,
        surveyType,
        _id: id,
        _version,
        visitedSurvey: visitedSurvey ?? [],
        currentSurvey: currentSurvey ?? _.last(visitedSurvey) ?? surveyMainKeyArr[0],
      };

      const age = moment().diff(patient.birth, 'years', false);
      // i 남성, j 임신, k 월경, l: 갱년기/유방, m 소아
      // 나이, 성별에 따른 설문 필터링
      // 활성화 조건 참고 (https://docs.google.com/spreadsheets/d/19BEm7UaxXJ5Mf-LJfHiqwF2qE8FQh_e95oGLrNrEXFw/edit#gid=67193016)
      if (age < 13) {
        // 만 13세 미만은 남녀 불문 소아 설문만
        surveyMainKeyArr = _.pull(surveyMainKeyArr, 'i', 'j', 'k', 'l');
      } else if (age >= 18 && patient.gender === Gender.Male) {
        // 만 18세 이상 남자는 남성 설문만
        surveyMainKeyArr = _.pull(surveyMainKeyArr, 'j', 'k', 'l', 'm');
      } else if (age >= 13 && patient.gender === Gender.Female) {
        // 만 13세 이상 여자는 임신, 월경, 갱년기/유방 설문만
        surveyMainKeyArr = _.pull(surveyMainKeyArr, 'i', 'm');
      } else {
        // 그 외에는 모두 삭제
        surveyMainKeyArr = _.pull(surveyMainKeyArr, 'i', 'j', 'k', 'l', 'm');
      }

      if (surveyType === 'B2C') {
        surveyMainKeyArr = _.pull(surveyMainKeyArr, 'A'); // B2C는 피로도 설문 생략
      }
      body.mainSurveyList = surveyMainKeyArr;
      const initFormData: any = {};
      surveyMainKeyArr?.forEach((sc) => {
        if (exceptYnSurveys.includes(sc)) {
          const survey = surveyObj[sc];
          if (survey) {
            const nextArr = (survey?.answers?.find((a) => a.value === 1)?.next as string[]) ?? [];
            nextArr?.forEach((next) => {
              if (body[next] === undefined) {
                body[next] = 0;
              }
              initFormData[next] = 0;
            });
          }
        } else if (body[sc] === undefined) {
          body[sc] = 0;
        }
        initFormData[sc] = 0;
      });
      // body.optionalSurveysSkipFlag = true;

      if (surveyType === 'B2C') {
        body.selectedMainSurveyCode = 0;
        body.mainSurveyList = surveyMainKeyArr;
        body.surveyList = _.filter(requiredSurveys, (k: string) => surveyMainKeyArr.includes(k));
      }

      setFormData({ ...body, initFormData, patient, initMainSurveyList: surveyMainKeyArr });
      if (mainSymptom || !_.isEmpty(visitedSurvey)) {
        // setPageIndex(Page.Review);
        // setPageIndex(Page.ServiceTerm);
        setPageIndex(Page.Continue);
      } else if (params.uuid && surveyType === 'B2C') {
        // UUID flow: 첫 진입 시 시작 화면(B2CMain) 노출. 버튼 클릭 시 DetailSurvey로 진입.
        setPageIndex(Page.B2CMain);
      } else if (surveyType === 'B2C') {
        setPageIndex(Page.DetailSurvey);
      } else {
        // setPageIndex(Page.Review);
        // setPageIndex(Page.ServiceTerm);
        setPageIndex(Page.Term);
      }
    }
  }, [updatedData?.id]);

  return {
    pageIndex,
    setPageIndex,
    getSurveyResData: surveyResData,
    surveyResData: updatedData,
    surveyRequestLoading,
    expiredDtRequestLoading,
    postSurvey,
  };
}
